/**
 * Tipos de la API de conversaciones (cnc-api: Conversacion, MensajeConversacion) y utilidades puras.
 * No pasan por BEI: el hilo trae objetos anidados que BEI no trata y no hay tabla ni formulario
 * genéricos. ESTE FICHERO ES IDÉNTICO EN cnc-client Y cnc-admin: si cambias uno, cambia el otro.
 */
export type EstadoConversacion = 'ABIERTA' | 'PROPUESTA_CIERRE' | 'CERRADA';
export type LadoConversacion = 'CLIENTE' | 'CNC';
/** Parámetro de la API para enlazar o filtrar por entidad. `solicitud` cubre escaneo e intraoral. */
export type TipoEntidadConversacion = 'pedido' | 'solicitud' | 'factura';
export type FiltroConversaciones = 'todas' | 'no-leidas' | 'abiertas' | 'pendientes' | 'propuestas' | 'cerradas';
/** `number` = ese grupo; `'sin'` = sin grupo; `null` = todos. */
export type FiltroGrupo = number | 'sin' | null;

export interface ReferenciaConversacion {
    tipo: 'pedido' | 'solicitud_escaneo' | 'solicitud_intraoral' | 'factura';
    id: number;
    etiqueta: string;
}

export interface ConversacionResumen {
    id: number;
    asunto: string;
    estado: EstadoConversacion;
    iniciadaPor: LadoConversacion;
    ultimoLado: LadoConversacion;
    fechaUltimoMensaje: string;
    fechaPropuestaCierre: string | null;
    fechaCierre: string | null;
    noLeida: boolean;
    pendienteRespuesta: boolean;
    referencia: ReferenciaConversacion | null;
    clienteNombre: string;
    grupoId: number | null;
    grupoNombre: string | null;
}

export interface AdjuntoConversacion {
    id: number;
    tipoArchivo: string;
    nombreFicheroOriginal: string;
    tamano: number;
}

export interface MensajeConversacion {
    id: number;
    tipo: 'MENSAJE' | 'EVENTO';
    lado: LadoConversacion;
    texto: string;
    fecha: string;
    autorVisible: string;
    /** Solo llega a empleados. */
    autorNombre?: string | null;
    adjuntos: AdjuntoConversacion[];
}

export interface PiezaFicha {
    codigoPaciente: string;
    trabajo: string | null;
    material: string | null;
    metodo: string | null;
}

/** Datos de lo que trata la conversación, para la ficha del hilo (Conversacion::getFicha). */
export interface FichaConversacion {
    fecha: string | null;
    estado: string | null;
    /** Pedido: alguna pieza pausada. El cliente lo ve como estado «PAUSADO». */
    pausado?: boolean;
    /** Pedido y diseño. */
    piezas?: PiezaFicha[];
    /** Factura. */
    numero?: string | null;
    importe?: number;
}

export interface ConversacionHilo extends ConversacionResumen {
    mensajes: MensajeConversacion[];
    /** Solo llega a empleados: pasos de grupo (y asignaciones antiguas). */
    eventosInternos?: MensajeConversacion[];
    /** Null en una consulta general. */
    ficha: FichaConversacion | null;
}

export interface EntidadConversacion {
    tipo: TipoEntidadConversacion;
    id: number;
    etiqueta: string;
}

export interface NuevaConversacion {
    asunto: string;
    texto: string;
    entidad?: EntidadConversacion | null;
    /** Solo CNC, y solo sin entidad. */
    clienteId?: number | null;
    grupoId?: number | null;
}

/** Grupo de empleados que atiende mensajes (GET /conversaciones/grupos). */
export interface GrupoConversacion {
    id: number;
    nombre: string;
    /** Solo en la respuesta a un empleado: si pertenece a ese grupo. */
    mio?: boolean;
}

export interface GruposConversacion {
    /** Solo en la respuesta a un empleado. */
    esAdmin?: boolean;
    grupos: GrupoConversacion[];
}

export interface IndicadorConversacion {
    abiertas: number;
    noLeidas: number;
    pendientes: number;
}

/** Casilla «Mensajes activados» de la ficha del cliente en cnc-admin: si su usuario está en el grupo Mensajes. */
export interface AccesoMensajesCliente {
    activos: boolean;
    /** Sin usuario no puede usar mensajes: la casilla sale desactivada. */
    conUsuario: boolean;
}

/** Aviso de CNC a varios clientes a la vez (GET/POST /conversaciones/comunicados). */
export interface ComunicadoResumen {
    id: number;
    asunto: string;
    texto: string;
    fecha: string;
    autorVisible: string;
    adjuntos: AdjuntoConversacion[];
    /** Solo cliente. */
    leido?: boolean;
    /** Solo CNC. */
    destinatarios?: number;
    retirado?: boolean;
    fechaRetirada?: string | null;
    autorNombre?: string | null;
}

export interface NuevoComunicado {
    asunto: string;
    texto: string;
}

export interface ResumenConversaciones {
    noLeidas?: number;
    pendientesRespuesta?: number;
    /** Solo cliente. */
    comunicadosNoLeidos?: number;
}

/** Lo que manda la API como autor de los mensajes de CNC (MensajeConversacion::AUTOR_CNC). */
export const AUTOR_CNC = 'CNC Dental';

export const ETIQUETAS_ESTADO: Record<EstadoConversacion, string> = {
    ABIERTA: 'Abierta',
    PROPUESTA_CIERRE: 'Propuesta para cierre',
    CERRADA: 'Cerrada'
};

/** Los mismos que acepta la API (AdjuntosConversacionService::TIPOS_MIME). */
export const EXTENSIONES_ADJUNTOS = ['jpg', 'jpeg', 'png', 'webp', 'pdf', 'stl', 'zip', 'docx', 'xlsx'];
export const ACEPTAR_ADJUNTOS = EXTENSIONES_ADJUNTOS.map(e => '.' + e).join(',');
export const MAX_MB_ADJUNTO = 20;
export const MAX_ADJUNTOS = 5;
export const MAX_MB_TOTAL_ADJUNTOS = 50;
const MB = 1024 * 1024;

/** Comprobación previa en el navegador, para no subir 50 MB y que luego la API diga que no. La que manda es la de la API. */
export function validarAdjuntos(archivos: File[]): string | null {
    if (archivos.length > MAX_ADJUNTOS) {
        return `Puedes adjuntar como mucho ${MAX_ADJUNTOS} archivos por mensaje.`;
    }
    let total = 0;
    for (const archivo of archivos) {
        const partes = archivo.name.split('.');
        const extension = partes.length > 1 ? partes[partes.length - 1].toLowerCase() : '';
        if (!EXTENSIONES_ADJUNTOS.includes(extension)) {
            return `«${archivo.name}»: tipo de archivo no permitido. Se admiten: ${EXTENSIONES_ADJUNTOS.join(', ')}.`;
        }
        if (archivo.size > MAX_MB_ADJUNTO * MB) {
            return `«${archivo.name}» pasa de ${MAX_MB_ADJUNTO} MB.`;
        }
        total += archivo.size;
    }
    if (total > MAX_MB_TOTAL_ADJUNTOS * MB) {
        return `Los adjuntos de un mensaje no pueden pasar de ${MAX_MB_TOTAL_ADJUNTOS} MB en total.`;
    }
    return null;
}

/**
 * Selección de adjuntos de un formulario (mensaje o alta), validada en cada cambio. Sin dependencias
 * de Angular: HiloConversacionComponent y NuevaConversacionComponent comparten esta lógica en vez de
 * duplicar elegir/quitar/validar cada uno por su lado.
 */
export class SeleccionAdjuntos {
    archivos: File[] = [];
    error: string | null = null;

    get hay(): boolean {
        return this.archivos.length > 0;
    }

    elegir(evento: Event): void {
        const input = evento.target as HTMLInputElement;
        this.archivos = [...this.archivos, ...Array.from(input.files || [])];
        input.value = '';
        this.error = validarAdjuntos(this.archivos);
    }

    quitar(indice: number): void {
        this.archivos = this.archivos.filter((_, i) => i !== indice);
        this.error = validarAdjuntos(this.archivos);
    }

    vaciar(): void {
        this.archivos = [];
        this.error = null;
    }
}

export function esImagen(adjunto: AdjuntoConversacion): boolean {
    return ['jpg', 'jpeg', 'png', 'webp'].includes((adjunto.tipoArchivo || '').toLowerCase());
}

export function tamanoLegible(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < MB) {
        return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / MB).toFixed(1).replace('.', ',')} MB`;
}

/** Sin LOCALE_ID en la app, el pipe number saldría con punto decimal. */
export function importeLegible(importe: number): string {
    return `${importe.toFixed(2).replace('.', ',')} €`;
}

/** Si el mensaje `indice` es el primero de su día (en la hora local), para poner delante la línea con la fecha. */
export function empiezaDia(mensajes: MensajeConversacion[], indice: number): boolean {
    return indice === 0
        || new Date(mensajes[indice].fecha).toDateString() !== new Date(mensajes[indice - 1].fecha).toDateString();
}

/** Mensajes y eventos internos intercalados por fecha y, a igual fecha, por id. */
export function mensajesEnOrden(hilo: ConversacionHilo): MensajeConversacion[] {
    return [...(hilo.mensajes || []), ...(hilo.eventosInternos || [])].sort((a, b) => {
        const diferencia = new Date(a.fecha).getTime() - new Date(b.fecha).getTime();
        return diferencia !== 0 ? diferencia : a.id - b.id;
    });
}

export function entidadDeReferencia(referencia: ReferenciaConversacion): EntidadConversacion {
    const tipo: TipoEntidadConversacion = referencia.tipo === 'pedido' || referencia.tipo === 'factura' ? referencia.tipo : 'solicitud';
    return {tipo, id: referencia.id, etiqueta: referencia.etiqueta};
}
